import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import { InstagramGlyph, TelegramGlyph } from "@/components/vangard/SocialGlyphs";

const MAFIA_PREFIXES = ["/mafia", "/host", "/dashboard", "/gamer", "/p/", "/profile"];

const CHANNELS = [
  {
    id: "telegram",
    href: "https://t.me/vangard_public",
    label: "Telegram",
    note: "vc.social.telegram-note",
    Glyph: TelegramGlyph,
  },
  {
    id: "instagram",
    href: "https://www.instagram.com/vangardclub_official",
    label: "Instagram",
    note: "vc.social.instagram-note",
    Glyph: InstagramGlyph,
  },
];

/**
 * Floating contact bubble: expands into Telegram / Instagram channel links.
 */
export default function SocialBubble() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (MAFIA_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;

  return (
    <div
      ref={wrapRef}
      className="fixed bottom-5 right-4 lg:bottom-7 lg:right-7 z-50 flex flex-col items-end gap-3"
      data-testid="social-bubble"
    >
      {open && (
        <div className="flex flex-col items-end gap-2.5" data-testid="social-bubble-panel">
          {CHANNELS.map(({ id, href, label, note, Glyph }, i) => (
            <a
              key={id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-bubble-${id}`}
              style={{ animationDelay: `${i * 70}ms` }}
              className="social-bubble-item group flex items-center gap-3 rounded-full border border-white/[0.12] bg-[#0C0C0C]/95 backdrop-blur-xl pl-4 pr-3 py-2.5 shadow-[0_18px_40px_-16px_rgba(0,0,0,0.9)] hover:border-white/30 hover:bg-[#141414] transition-colors"
            >
              <span className="text-right">
                <span className="block text-[12px] text-white leading-none">{label}</span>
                <span className="block text-[10px] tracking-[0.2em] uppercase text-neutral-500 mt-1 leading-none">
                  {t(note)}
                </span>
              </span>
              <Glyph size={30} />
            </a>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t("vc.social.aria")}
        data-testid="social-bubble-trigger"
        className="group relative inline-flex items-center gap-2.5 h-14 pl-4 pr-5 rounded-full bg-white text-black shadow-[0_18px_44px_-14px_rgba(255,255,255,0.45)] hover:-translate-y-0.5 hover:shadow-[0_22px_52px_-12px_rgba(255,255,255,0.6)] transition-all duration-300"
      >
        <span className="relative flex items-center justify-center">
          {open ? <X size={21} strokeWidth={1.8} /> : <MessageCircle size={21} strokeWidth={1.8} />}
          {!open && (
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E5484D] opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#E5484D] ring-2 ring-white" />
            </span>
          )}
        </span>
        <span className="text-[11px] tracking-[0.25em] uppercase font-medium">{t("vc.social.cta")}</span>
      </button>
    </div>
  );
}
